import * as React from 'react';
import WaveSurfer from 'wavesurfer.js';
import { WaveformData } from '../models';
import CursorPlugin from 'wavesurfer.js/src/plugin/cursor';
import RegionsPlugin, { Region } from 'wavesurfer.js/src/plugin/regions';
import MinimapPlugin from 'wavesurfer.js/src/plugin/minimap';
import { Annotation } from '@gen/proto/lr/transcript/v1alpha1/transcript_pb';
import { durationObjectToFloat, floatToDurationObject } from '../helpers';
import { handleWaveformScroll } from './waveform-utils';

import './AnnotationWaveform.css';
import { WaveSurferParams } from 'wavesurfer.js/types/params';
import TimelinePlugin from 'wavesurfer.js/src/plugin/timeline';

const convertRegionsToAnnotations = (regions: Region[]): Annotation.AsObject[] => {
  return regions.map((region) => ({
    startTime: floatToDurationObject(region.start),
    endTime: floatToDurationObject(region.end),
    label: (region.data?.text ? region.data.text : '') as string,
  }));
};

const wavesurferStaticProps: Partial<WaveSurferParams> = {
  backend: 'MediaElement',
  removeMediaElementOnDestroy: false,
  scrollParent: true,
  hideScrollbar: true,
  autoCenter: true,
  autoCenterRate: 100,
  mediaControls: true,
  progressColor: '#0065b3',
  barWidth: 4,
  barMinHeight: 0.5,
  partialRender: true,
  maxCanvasWidth: 10000,
};

function useWaveSurfer(props: {
  containerRef: React.RefObject<HTMLElement>;
  playerRef: React.RefObject<HTMLMediaElement>;
  timelineRef: React.RefObject<HTMLDivElement>;
  waveformData: WaveformData;
  zoom: number;
}) {
  const wavesurfer = React.useRef<WaveSurfer>();
  const { containerRef, playerRef, timelineRef, waveformData, zoom } = props;
  const serializedWaveformData = JSON.stringify(waveformData);

  // Initialize WaveSurfer
  React.useLayoutEffect(() => {
    const container = containerRef.current;

    if (container && playerRef.current && timelineRef.current) {
      wavesurfer.current = WaveSurfer.create({
        ...wavesurferStaticProps,
        container: container,
        minPxPerSec: zoom,
        duration: playerRef.current.duration,
        plugins: [
          CursorPlugin.create({
            showTime: false,
            followCursorY: 'true',
          }),
          RegionsPlugin.create({
            name: 'regions',
            dragSelection: true,
            removeButton: true,
            contentEditable: true,
          }),
          MinimapPlugin.create({
            height: 20,
            barMinHeight: null,
          }),
          TimelinePlugin.create({
            container: timelineRef.current,
            height: 10,
          }),
        ],
      });
    }

    if (wavesurfer.current && playerRef.current && waveformData.data) {
      wavesurfer.current.load(playerRef.current, waveformData.data);
    }

    return function cleanup() {
      if (container) {
        if (wavesurfer.current) {
          try {
            wavesurfer.current.destroy();
          } catch (e) {
            // ignore errors on destroy
          }
        }
      }
    };
  }, [containerRef, waveformData.data, serializedWaveformData, playerRef, zoom, timelineRef]);

  return wavesurfer;
}

function useWaveSurferEventListeners(props: {
  wavesurfer: React.RefObject<WaveSurfer | undefined>;
  onUpdate: AnnotationWaveformProps['onUpdate'];
  redrawingRef: React.RefObject<boolean>;
}) {
  const { wavesurfer, onUpdate, redrawingRef } = props;

  React.useEffect(() => {
    const ws = wavesurfer.current;

    function saveRegions() {
      if (ws?.regions.list) {
        onUpdate && onUpdate(convertRegionsToAnnotations(Object.values(ws?.regions.list)));
      }
    }

    function handleRegionRemoved() {
      if (!redrawingRef.current) {
        saveRegions();
      }
    }

    function handleRegionUpdated() {}

    function handleRegionUpdateEnd(region?: Region) {
      if (region && (!region.data || !region.data?.text)) {
        const el = region.element?.getElementsByClassName('region-content')[0] as HTMLDivElement | undefined | null;
        el?.focus();
        const handleContentBlur = (event: Event) => {
          saveRegions();
          event.target?.removeEventListener('blur', handleContentBlur);
        };
        el?.addEventListener('blur', handleContentBlur);
      } else {
        saveRegions();
      }
    }

    function handleRegionClick(region: Region, e: PointerEvent) {
      if (e.ctrlKey || e.metaKey) {
        e.stopPropagation(); // prevent triggering a click on the waveform
        region.play(0);
      }
    }

    if (ws) {
      ws.on('region-removed', handleRegionRemoved);
      ws.on('region-updated', handleRegionUpdated);
      ws.on('region-update-end', handleRegionUpdateEnd);
      ws.on('region-click', handleRegionClick);
    }

    return function cleanup() {
      ws?.un('region-removed', handleRegionRemoved);
      ws?.un('region-updated', handleRegionUpdated);
      ws?.un('region-update-end', handleRegionUpdateEnd);
      ws?.un('region-click', handleRegionClick);
    };
  }, [wavesurfer, onUpdate, redrawingRef]);
}

type AnnotationWaveformProps = {
  playerRef: React.RefObject<HTMLMediaElement>;
  waveformData: WaveformData;
  annotations: Annotation.AsObject[];
  // handle all annotations
  onUpdate?: (annotations: Annotation.AsObject[]) => void;
  // In px per sec
  initialZoom?: number;
  hide?: boolean;
};

/* eslint-disable max-lines-per-function */
function AnnotationWaveform(props: AnnotationWaveformProps) {
  const initialZoom = props.initialZoom ?? 20;
  const [zoom, setZoom] = React.useState(initialZoom);

  const waveformRef = React.useRef<HTMLDivElement>(null);
  const timelineRef = React.useRef<HTMLDivElement>(null);
  const wavesurfer = useWaveSurfer({
    containerRef: waveformRef,
    waveformData: props.waveformData,
    zoom: initialZoom,
    playerRef: props.playerRef,
    timelineRef,
  });

  const { onUpdate, annotations } = props;

  // Workaround WS's region-removed being called when rerendering when the annotations prop changes.
  const redrawingRef = React.useRef<boolean>(false);

  // Install event listeners for wavesurfer
  useWaveSurferEventListeners({
    wavesurfer,
    onUpdate,
    redrawingRef,
  });

  const serializedAnnotations = JSON.stringify(props.annotations);

  React.useEffect(() => {
    const ws = wavesurfer.current;

    annotations.forEach((annotation) => {
      if (!ws) {
        return;
      }
      const { startTime, endTime, label } = annotation;

      ws.addRegion({
        start: durationObjectToFloat(startTime),
        end: durationObjectToFloat(endTime),
        data: {
          text: label,
        },
        resize: true,
      });
    });

    redrawingRef.current = false;

    return function cleanup() {
      if (ws?.isReady) {
        redrawingRef.current = true;
        ws.clearRegions();
      }
    };
  }, [wavesurfer, wavesurfer.current?.isReady, annotations, serializedAnnotations, redrawingRef]);

  // Register handler for zooming and seeking using scroll
  React.useEffect(() => {
    const wf = waveformRef.current;

    const handleScroll = (event: WheelEvent) => {
      if (!wavesurfer.current) return;
      handleWaveformScroll(wavesurfer.current, zoom, (newZoom) => setZoom(newZoom), event);
    };

    wf?.addEventListener('wheel', handleScroll);

    return function cleanup() {
      wf?.removeEventListener('wheel', handleScroll);
    };
  }, [zoom, wavesurfer, waveformRef]);

  // Disable inputting multiline labels on regions
  React.useEffect(() => {
    const wf = waveformRef.current;
    const ws = wavesurfer.current;

    const handleEnter = (event: KeyboardEvent) => {
      if (waveformRef.current) {
        if (event.key === 'Enter') {
          event.preventDefault();
          window.getSelection()?.removeAllRanges();

          // A bit of a hack... since region-updated handlers expect to be passed the region. But we're only calling
          // `saveRegions()`, which operates on *all* regions every time anyway.
          (event.target as HTMLDivElement).blur();
          ws?.fireEvent('region-update-end');
        }
      }
    };

    wf?.addEventListener('keydown', handleEnter, { capture: true });

    return function cleanup() {
      wf?.removeEventListener('keydown', handleEnter);
    };
  }, [waveformRef, wavesurfer]);

  return (
    <div className={`annotation-waveform-container ${props.hide ? 'hidden' : ' '}`}>
      <div ref={timelineRef} />
      <div ref={waveformRef} />
    </div>
  );
}

export default AnnotationWaveform;
